import React from 'react';
import {
  WT_PRIORITY_LABELS,
  WT_TOKENS,
  WT_TONE_COLORS,
} from '../../warm-transfer/config';
import type { WarmTransfer } from '../../warm-transfer/types';
import { formatOnCall, useLiveWait } from '../../warm-transfer/utils';
import { Icon } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { WarmTransferAICallSummary } from './WarmTransferAICallSummary';

function PriorityPill({ status }: { status: WarmTransfer['status'] }) {
  const cfg = WT_PRIORITY_LABELS[status];
  const tone = WT_TONE_COLORS[cfg.tone];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-[11px] py-[3px] text-[12.5px] font-semibold tracking-[-0.005em]"
      style={{ background: tone.bg, color: tone.c }}
    >
      <span className="rounded-full" style={{ width: 6, height: 6, background: tone.c }} />
      {cfg.label}
    </span>
  );
}

function PatientInfoBar({ info }: { info: WarmTransfer['info'] }) {
  const cells = [
    { label: 'Date of birth', value: info.dob, icon: 'calendar' as const, mono: false, highlight: false },
    { label: 'Phone', value: info.phone, icon: 'phone' as const, mono: true, highlight: false },
    { label: 'Insurance', value: info.insurance, icon: 'book' as const, mono: false, highlight: true },
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {cells.map((cell) => (
        <div
          key={cell.label}
          className="flex flex-col gap-2 rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-[18px] py-[15px] shadow-[var(--sh-1)]"
        >
          <div className="wt-eyebrow inline-flex items-center gap-[7px] text-[9.5px]">
            <Icon kind={cell.icon} size={12.5} color="var(--ink-2)" stroke={2} />
            {cell.label}
          </div>
          <div
            className={cell.mono ? 'wt-mono' : ''}
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: cell.highlight ? WT_TOKENS.greenDeep : 'var(--ink-0)',
              letterSpacing: cell.mono ? 0 : '-0.01em',
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cell.value}
          </div>
        </div>
      ))}
    </div>
  );
}

export interface WarmTransferDetailPanelProps {
  transfer: WarmTransfer;
  isHandled: boolean;
}

export function WarmTransferDetailPanel({ transfer, isHandled }: WarmTransferDetailPanelProps) {
  const liveDuration = useLiveWait(transfer.duration);

  return (
    <div className="wt-fade-up wt-d-2 flex min-w-0 flex-1 flex-col gap-5" key={transfer.id}>
      <header className="relative overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]">
        <div className="absolute left-0 right-0 top-0 h-1" style={{ background: WT_TOKENS.iriLinear }} />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ background: WT_TOKENS.iriLinear }}
        />
        <div className="relative flex flex-col gap-4 px-6 py-[22px]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-[11px]">
                <h2 className="m-0 whitespace-nowrap text-[28px] font-semibold leading-[1.1] tracking-[-0.03em] text-[var(--ink-0)]">
                  {transfer.patient.name}
                </h2>
                <PriorityPill status={transfer.status} />
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--cream-2)] bg-[var(--cream-1)] px-[11px] py-[3px] text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]">
                  <Icon kind="phone" size={12} color="var(--ink-2)" stroke={2} />
                  Phone call
                </span>
              </div>
              <div className="mt-2 flex items-center gap-[7px] text-[13.5px] text-[var(--ink-2)]">
                <span className="font-medium text-[var(--ink-1)]">{transfer.reason}</span>
                <span style={{ color: WT_TOKENS.ink5 }}>·</span>
                {isHandled ? (
                  <span>{transfer.ago}</span>
                ) : (
                  <span className="inline-flex items-center gap-1.5">
                    <Icon kind="clock" size={12.5} color={WT_TOKENS.ink4} stroke={2} />
                    <span>on call</span>
                    <span className="wt-mono font-bold text-[var(--ink-0)]">
                      {formatOnCall(liveDuration)}
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-none items-center gap-2">
              {!isHandled ? (
                <button
                  type="button"
                  className="inline-flex h-[42px] cursor-pointer items-center justify-center gap-[7px] rounded-[10px] border px-5 text-[14px] font-semibold tracking-[-0.005em] text-white"
                  style={{
                    borderColor: WT_TOKENS.greenDeep,
                    background: WT_TOKENS.green,
                    boxShadow: WT_TOKENS.shadowXs,
                  }}
                >
                  <Icon kind="check-circ" size={15} stroke={2} color="#fff" />
                  Pick up call
                </button>
              ) : (
                <span
                  className="inline-flex h-[42px] items-center gap-[7px] rounded-[10px] px-[18px] text-[13.5px] font-semibold"
                  style={{ background: WT_TOKENS.greenSoft, color: WT_TOKENS.greenDeep }}
                >
                  <Icon kind="check-circ" size={15} stroke={2} />
                  Resolved
                </span>
              )}
              <IconButton
                icon="dots"
                size={42}
                label="More actions"
                className="rounded-[10px] border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
              />
            </div>
          </div>

          {!isHandled && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="inline-flex h-8 cursor-pointer items-center gap-[7px] rounded-[9px] border px-3 text-[12.5px] font-semibold tracking-[-0.005em]"
                style={{
                  background: WT_TOKENS.brassTint,
                  color: WT_TOKENS.brassDeep,
                  borderColor: WT_TOKENS.brassSoft,
                }}
              >
                <span className="rounded-full" style={{ width: 7, height: 7, background: WT_TOKENS.brass }} />
                Open
                <Icon kind="down" size={13} color={WT_TOKENS.brassDeep} stroke={2} />
              </button>
              <button
                type="button"
                className="inline-flex h-8 cursor-pointer items-center gap-[7px] rounded-[9px] border border-dashed px-3 text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]"
                style={{ borderColor: 'rgba(28, 24, 18, 0.24)' }}
              >
                <Icon kind="team" size={14} color="var(--ink-2)" stroke={2} />
                Assign
                <Icon kind="down" size={13} color="var(--ink-2)" stroke={2} />
              </button>
              <button
                type="button"
                className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-[9px] border px-3.5 text-[12.5px] font-semibold tracking-[-0.005em]"
                style={{
                  background: WT_TOKENS.greenWash,
                  color: WT_TOKENS.greenDeep,
                  borderColor: WT_TOKENS.greenSoft,
                }}
              >
                Assign to me
              </button>
            </div>
          )}
        </div>
      </header>

      <PatientInfoBar info={transfer.info} />
      <WarmTransferAICallSummary points={transfer.aiSummary} />

      {!isHandled ? (
        <div className="mt-1 flex justify-center">
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-[7px] border-0 bg-transparent text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]"
          >
            <Icon kind="chat" size={13} stroke={2} />
            View full call transcript
          </button>
        </div>
      ) : (
        <div className="mt-1 flex flex-col items-center gap-3.5">
          <div
            className="inline-flex items-center gap-2 rounded-[10px] px-[18px] py-2.5 text-[13px] font-semibold tracking-[-0.005em]"
            style={{ background: WT_TOKENS.greenSoft, color: WT_TOKENS.greenDeep }}
          >
            <Icon kind="check-circ" size={14} stroke={2} />
            This transfer has been resolved
          </div>
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-[7px] border-0 bg-transparent text-[12.5px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]"
          >
            <Icon kind="chat" size={13} stroke={2} />
            View full call transcript
          </button>
        </div>
      )}
    </div>
  );
}
